import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, User, Tag, ChevronRight, ChevronLeft, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const currentPage = parseInt(searchParams.get('page') || '1');
  const currentCategory = searchParams.get('category') || '';
  const currentTag = searchParams.get('tag') || '';
  const LIMIT = 12;

  useEffect(() => {
    fetchArticles();
    fetchCategories();
  }, [currentPage, currentCategory, currentTag]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', LIMIT);
      if (currentCategory) params.append('category', currentCategory);
      if (currentTag) params.append('tag', currentTag);

      const [articlesRes, countRes] = await Promise.all([
        axios.get(`${API}/blog/articles?${params}`),
        axios.get(`${API}/blog/articles/count?${currentCategory ? `category=${currentCategory}` : ''}${currentTag ? `&tag=${currentTag}` : ''}`)
      ]);
      setArticles(articlesRes.data);
      setTotalCount(countRes.data.count);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/blog/categories`);
      setCategories(res.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const totalPages = Math.ceil(totalCount / LIMIT);

  const setFilter = (key, value) => {
    const params = new URLSearchParams();
    if (key === 'category' && value) params.set('category', value);
    if (key === 'tag' && value) params.set('tag', value);
    params.set('page', '1');
    setSearchParams(params);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="blog-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Home</span>
            </button>
            <span className="font-['Playfair_Display'] text-xl font-bold text-slate-900">Blog</span>
            <div className="w-16" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {/* Hero */}
        <div className="text-center mb-10">
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-slate-900 mb-3">
            Exam Ready <span className="text-orange-600">Blog</span>
          </h1>
          <p className="text-base text-slate-600 max-w-2xl mx-auto">
            Tips, strategies, and insights to help you succeed in your Cambridge English exam
          </p>
          <p className="text-sm text-orange-600 max-w-2xl mx-auto mt-1">
            Consejos, estrategias y conocimientos para ayudarte a aprobar tu examen Cambridge de inglés
          </p>
        </div>

        {/* Category Filter */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center mb-10" data-testid="blog-categories">
            <button
              onClick={() => setFilter('category', '')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                !currentCategory
                  ? 'bg-slate-900 text-white'
                  : 'bg-white border border-slate-300 text-slate-600 hover:border-slate-400'
              }`}
            >
              All
            </button>
            {categories.map(cat => (
              <button
                key={cat.name}
                onClick={() => setFilter('category', cat.name)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  currentCategory === cat.name
                    ? 'bg-slate-900 text-white'
                    : 'bg-white border border-slate-300 text-slate-600 hover:border-slate-400'
                }`}
              >
                {cat.name} ({cat.count})
              </button>
            ))}
          </div>
        )}

        {/* Active tag filter */}
        {currentTag && (
          <div className="flex items-center gap-2 justify-center mb-8">
            <Tag className="w-4 h-4 text-orange-500" />
            <span className="text-sm text-slate-600">Tag:</span>
            <span className="text-sm font-semibold text-slate-900">{currentTag}</span>
            <button
              onClick={() => setFilter('tag', '')}
              className="text-xs text-red-500 hover:text-red-700 ml-2 underline"
            >
              Clear
            </button>
          </div>
        )}

        {/* Articles Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-lg overflow-hidden animate-pulse">
                <div className="h-48 bg-slate-200" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-slate-200 rounded w-1/3" />
                  <div className="h-6 bg-slate-200 rounded w-full" />
                  <div className="h-4 bg-slate-200 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">No articles yet</h2>
            <p className="text-slate-500">Check back soon for new content!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-articles-grid">
            {articles.map(article => (
              <article
                key={article.id}
                onClick={() => navigate(`/blog/${article.slug}`)}
                className="bg-white border border-slate-200 rounded-lg overflow-hidden cursor-pointer group hover:shadow-lg transition-shadow"
                data-testid={`blog-article-${article.slug}`}
              >
                {article.featured_image ? (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={article.featured_image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ) : (
                  <div className="h-48 bg-gradient-to-br from-orange-100 to-amber-50 flex items-center justify-center">
                    <span className="font-['Playfair_Display'] text-4xl text-orange-300 font-bold">ER</span>
                  </div>
                )}
                <div className="p-5">
                  {article.categories?.length > 0 && (
                    <div className="flex gap-2 mb-2">
                      {article.categories.slice(0, 2).map(cat => (
                        <span key={cat} className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                          {cat}
                        </span>
                      ))}
                    </div>
                  )}
                  <h2 className="font-['Playfair_Display'] text-lg font-bold text-slate-900 mb-2 group-hover:text-orange-700 transition-colors line-clamp-2">
                    {article.title}
                  </h2>
                  {article.excerpt && (
                    <p className="text-sm text-slate-500 line-clamp-3 mb-3">{article.excerpt}</p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    {article.author && (
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {article.author}
                      </span>
                    )}
                    {article.published_at && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(article.published_at)}
                      </span>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-10" data-testid="blog-pagination">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setSearchParams(prev => { prev.set('page', currentPage - 1); return prev; })}
              className="rounded-full"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-slate-600">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setSearchParams(prev => { prev.set('page', currentPage + 1); return prev; })}
              className="rounded-full"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
